import React, { useState, useEffect, useCallback } from 'react';
import { X, Share2 } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { useSound } from '../context/SoundContext';

const STORAGE_KEY = 'dios10_hideAddToHomePrompt';

const isMobileIOS = () => /iPhone|iPad|iPod/i.test(navigator.userAgent);
const isMobileAndroid = () => /Android/i.test(navigator.userAgent);
const isMobile = () => isMobileIOS() || isMobileAndroid();

/** Returns browser id for instruction lookup: safari, chrome, firefox, edge, samsung, opera, default */
const getMobileBrowser = () => {
  const ua = navigator.userAgent;
  if (/CriOS|Chrome/i.test(ua) && !/Edge|Edg|OPR|SamsungBrowser|Firefox|FxiOS/i.test(ua)) return 'chrome';
  if (/FxiOS|Firefox/i.test(ua)) return 'firefox';
  if (/EdgiOS|Edg/i.test(ua)) return 'edge';
  if (/SamsungBrowser/i.test(ua)) return 'samsung';
  if (/OPiOS|OPR/i.test(ua)) return 'opera';
  if (/Safari/i.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua)) return 'safari';
  return 'default';
};

const AddToHomeScreenModal = ({ show, onClose }) => {
  const { t } = useLanguage();
  const { playSound } = useSound();
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleAddToHome = useCallback(async () => {
    playSound('click');
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setDeferredPrompt(null);
      }
      onClose();
    } else if (isMobileIOS()) {
      // iOS: mostra istruzioni per l'utente
      // Safari: Condividi > Aggiungi a Home
      onClose();
    } else if (isMobileAndroid()) {
      // Android Chrome: menu > Aggiungi a Home
      onClose();
    } else {
      onClose();
    }
  }, [deferredPrompt, onClose, playSound]);

  const handleDontShowAgain = useCallback(() => {
    playSound('click');
    try {
      localStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {}
    onClose();
  }, [onClose, playSound]);

  if (!show) return null;

  return (
    <div className="add-to-home-overlay" role="dialog" aria-modal="true" aria-labelledby="add-to-home-title">
      <div className="add-to-home-modal">
        <button
          type="button"
          className="add-to-home-close modal-close-btn"
          onClick={() => { playSound('click'); onClose(); }}
          aria-label={t('common.cancel')}
        >
          <X className="w-5 h-5" />
        </button>

        <h2 id="add-to-home-title" className="add-to-home-title">
          {t('auth.addToHomeTitle')}
        </h2>
        <p className="add-to-home-message">
          {t('auth.addToHomeMessage')}
        </p>

        {((isMobileIOS() || isMobileAndroid()) && !deferredPrompt) && (() => {
          const browser = getMobileBrowser();
          const key = `auth.addToHomeSteps${browser.charAt(0).toUpperCase() + browser.slice(1)}`;
          const steps = t(key);
          return (
            <div className="add-to-home-ios-steps">
              {steps === key ? t('auth.addToHomeStepsDefault') : steps}
            </div>
          );
        })()}

        <div className="add-to-home-actions">
          <button
            type="button"
            className="add-to-home-btn add-to-home-btn-primary"
            onClick={handleAddToHome}
          >
            {deferredPrompt && <Share2 className="w-4 h-4 mr-2" />}
            {deferredPrompt ? t('auth.addToHomeButton') : t('auth.addToHomeGotIt')}
          </button>
          <button
            type="button"
            className="add-to-home-btn add-to-home-btn-secondary"
            onClick={handleDontShowAgain}
          >
            {t('auth.addToHomeDontShowAgain')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddToHomeScreenModal;
export { isMobile, isMobileIOS, isMobileAndroid, getMobileBrowser, STORAGE_KEY };
